/** Prevents duplicate auto-delete runs when multiple <cart-items> instances listen to cart:updated. */
let cartAutoDeleteLock = false;

class CartCheckoutBusy {
  constructor() {
    this.pending = 0;
    this.timer = null;
    this.timeoutMs = 60000;
    this.selector = [
      'mini-cart .button-container button[name="checkout"]',
      "mini-cart .button-container button[data-delivery-address-open]",
      '#main-cart-footer .cart__checkout-button[name="checkout"]',
      "#main-cart-footer .cart__checkout-button[data-delivery-address-open]",
      '.sticky-checkout .cart__checkout-button[name="checkout"]',
      ".sticky-checkout .cart__checkout-button[data-delivery-address-open]",
    ].join(", ");

    document.addEventListener("cart:updated", () => {
      if (this.pending > 0) this.setBusy(true);
    });
    document.addEventListener("ajaxProduct:added", () => {
      if (this.pending > 0) this.setBusy(true);
    });
  }

  start() {
    this.pending += 1;
    if (this.pending === 1) {
      this.setBusy(true);
      this.timer = setTimeout(() => this.stop(true), this.timeoutMs);
    }
  }

  /** @param {boolean} [force] full unlock (errors / timeout) */
  stop(force = false) {
    this.pending = force ? 0 : Math.max(0, this.pending - 1);
    if (this.pending > 0) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.setBusy(false);
  }

  get isBusy() {
    return this.pending > 0;
  }

  setBusy(on) {
    document.querySelectorAll(this.selector).forEach((btn) => {
      btn.classList.toggle("loading", on);
      if (on) {
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
        return;
      }

      const terms = btn
        .closest(".button-container, .summary_buttons, .js-contents-sticky-checkout")
        ?.querySelector("cart-terms-acceptance");
      if (terms?.syncCheckoutState) {
        terms.syncCheckoutState();
        return;
      }

      const empty = btn.dataset.cartEmpty === "true";
      btn.disabled = empty;
      btn.setAttribute("aria-disabled", String(empty));
    });
  }
}

const cartCheckoutBusy = new CartCheckoutBusy();
window.cartCheckoutBusy = cartCheckoutBusy;

/**
 * Gift tier mins (cents) removed because the cart fell below them.
 * Blocks auto-add of those tiers until qualifying spend reaches them again —
 * stops "delete T2 → add T1+T2 → delete T2" when stepping down.
 */
const blockedGiftThresholds = new Set();

function syncBlockedGiftThresholds(qualifying) {
  if (!Number.isFinite(qualifying)) return;
  for (const threshold of [...blockedGiftThresholds]) {
    if (qualifying >= threshold) blockedGiftThresholds.delete(threshold);
  }
}

function blockGiftThreshold(limit) {
  const n = Math.round(Number(limit));
  if (Number.isFinite(n) && n > 0) blockedGiftThresholds.add(n);
}

function isGiftThresholdBlocked(limit) {
  const n = Math.round(Number(limit));
  return Number.isFinite(n) && blockedGiftThresholds.has(n);
}

function readGiftLines(root) {
  return [...root.querySelectorAll("[data-line-key][data-delete-under]")].map((el) => ({
    key: el.dataset.lineKey,
    quantity: Number(el.dataset.quantity),
    limit: Math.round(Number(el.dataset.deleteUnder)),
    valid: el.dataset.giftValid !== "false",
  }));
}

function planGiftLineUpdates(lines, { qualifying, stack, thresholds = false }) {
  const tiers = [...new Set(lines.filter((l) => l.valid).map((l) => l.limit))].sort((a, b) => a - b);
  const keptTiers = new Set();
  const updates = {};

  for (const line of lines) {
    let keep = line.valid && !keptTiers.has(line.limit);
    if (keep && thresholds) {
      const nextTier = tiers.find((t) => t > line.limit);
      if (qualifying < line.limit) {
        keep = false;
        blockGiftThreshold(line.limit);
      } else if (!stack && nextTier && qualifying >= nextTier) {
        keep = false;
      }
    }

    if (!keep) {
      updates[line.key] = 0;
      continue;
    }
    keptTiers.add(line.limit);
    if (line.quantity > 1) updates[line.key] = 1;
  }
  return updates;
}

/**
 * `/cart/add.js` puts `items`, `total_price`, and `sections` on the same object. Prefer JSON; if missing,
 * parse `sections["mini-cart"]` (expects `data-cart-total-cents` / `data-delete-under` from mini-cart.liquid).
 * Prefer `data-gift-qualifying-total-cents` for gift auto-delete when present (department-scoped campaigns).
 */
function getGiftEligibilityTotal(cart, root) {
  const fromCart = Number(cart?.gift_qualifying_total);
  if (Number.isFinite(fromCart)) return fromCart;

  const scope = root || document;
  const el =
    scope.querySelector("[data-gift-qualifying-total-cents]") ||
    document.querySelector("[data-gift-qualifying-total-cents]");
  if (el) {
    const n = Number(el.getAttribute("data-gift-qualifying-total-cents"));
    if (Number.isFinite(n)) return n;
  }

  return Number(cart?.total_price);
}

function getCartPayloadForAutoDelete(product) {
  if (!product) return null;

  const productItems = product.items;
  const totalPrice = Number(product.total_price);
  if (Array.isArray(productItems) && productItems.length > 0 && Number.isFinite(totalPrice)) {
    const html = product.sections?.["mini-cart"];
    let giftQualifyingTotal;
    if (typeof html === "string" && html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const qualEl = doc.querySelector("[data-gift-qualifying-total-cents]");
      if (qualEl) {
        const n = Number(qualEl.getAttribute("data-gift-qualifying-total-cents"));
        if (Number.isFinite(n)) giftQualifyingTotal = n;
      }
    }
    return {
      total_price: totalPrice,
      gift_qualifying_total: giftQualifyingTotal,
      items: productItems,
    };
  }

  const html = product.sections?.["mini-cart"];
  if (typeof html !== "string" || !html) return null;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const subtotalEl = doc.querySelector("[data-cart-total-cents]");
  const totalCents = subtotalEl ? Number(subtotalEl.getAttribute("data-cart-total-cents")) : NaN;
  if (!Number.isFinite(totalCents)) return null;

  const qualEl = doc.querySelector("[data-gift-qualifying-total-cents]");
  const giftQualifyingTotal = qualEl ? Number(qualEl.getAttribute("data-gift-qualifying-total-cents")) : undefined;

  const parsedItems = [];
  doc.querySelectorAll(".mini-cart__navigation > li[data-variant-id]").forEach((li) => {
    const raw = li.getAttribute("data-delete-under");
    const properties = {};
    if (raw != null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n)) properties._delete_under = n;
    }
    parsedItems.push({ properties });
  });

  if (!parsedItems.length) return null;

  return {
    total_price: totalCents,
    gift_qualifying_total: Number.isFinite(giftQualifyingTotal) ? giftQualifyingTotal : undefined,
    items: parsedItems,
  };
}

/**
 * Cart page full reload does not fire `ajaxProduct:added`. Build a cart:updated-like
 * event from DOM so gift auto-delete / auto-add can run after load.
 */
function getCartPageAutoGiftEvent() {
  if (!document.body.classList.contains("template-cart")) return null;

  const totalEl = document.querySelector("[data-cart-total-cents]");
  const totalCents = totalEl ? Number(totalEl.getAttribute("data-cart-total-cents")) : NaN;
  if (!Number.isFinite(totalCents)) return null;

  const qualEl = document.querySelector("[data-gift-qualifying-total-cents]");
  const giftQualifyingTotal = qualEl ? Number(qualEl.getAttribute("data-gift-qualifying-total-cents")) : undefined;

  const items = [];
  document.querySelectorAll("table.cart-items tbody tr.cart-item").forEach((el) => {
    const raw = el.getAttribute("data-delete-under");
    const properties = {};
    if (raw != null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n)) properties._delete_under = n;
    }
    items.push({ properties });
  });

  if (!items.length) return null;

  return new CustomEvent("cart:updated", {
    detail: {
      cart: {
        total_price: totalCents,
        gift_qualifying_total: Number.isFinite(giftQualifyingTotal) ? giftQualifyingTotal : undefined,
        items,
      },
    },
  });
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      this.closest("cart-items").updateQuantity(this.dataset.index, 0);

      document.dispatchEvent(
        new CustomEvent("ajaxProduct:removed", {
          detail: {
            product: {
              id: this.dataset.productId,
            },
          },
        }),
      );
    });
  }
}
customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById("shopping-cart-line-item-status");
    this.cartErrors = document.getElementById("cart-errors");

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0,
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", this.debouncedOnChange.bind(this));
    const sbCartUpsell = this.closest("mini-cart")?.querySelector("sb-cart-upsell[data-rebuy-enabled='true']");
    if (sbCartUpsell) {
      setTimeout(() => {
        sbCartUpsell.fetchRebuyProducts();
      }, 500);
    }
  }

  triggerSingleGiftAutoAdd() {
    if (cartAutoDeleteLock) return;

    const root = this.closest("mini-cart") || document;
    const motivationalBar = root.querySelector(".motivational-bar");
    const autoAddEnabled = motivationalBar?.getAttribute("data-auto-add-gift-products") === "true";
    if (!autoAddEnabled) return;

    const autoAddButton = root.querySelector(
      'add-to-cart.motivational-bar__gift-button[data-auto-add-gift="true"]:not([disabled]):not([data-auto-add-fired="true"])',
    );
    if (!autoAddButton) return;

    let tierMin = NaN;
    try {
      const props = JSON.parse(autoAddButton.dataset?.customProperties ?? "{}");
      tierMin = Number(props._delete_under);
    } catch {
      /* fall through */
    }

    const qualAttr = motivationalBar.getAttribute("data-gift-qualifying-total-cents");
    const totalAttr = root.querySelector("[data-cart-total-cents]")?.getAttribute("data-cart-total-cents");
    const qualifying = getGiftEligibilityTotal(
      {
        gift_qualifying_total: qualAttr != null && qualAttr !== "" ? Number(qualAttr) : undefined,
        total_price: totalAttr != null && totalAttr !== "" ? Number(totalAttr) : undefined,
      },
      root,
    );

    syncBlockedGiftThresholds(qualifying);

    // Never auto-add a tier the cart no longer qualifies for (or that we just removed).
    if (Number.isFinite(tierMin) && tierMin > 0) {
      if (Number.isFinite(qualifying) && qualifying < tierMin) return;
      if (isGiftThresholdBlocked(tierMin)) return;
    }

    autoAddButton.setAttribute("data-auto-add-fired", "true");
    autoAddButton.click();
  }

  /**
   * Removes free-gift lines using `_delete_under` (cents). Compares against department-scoped
   * gift qualifying total when `data-gift-qualifying-total-cents` / `gift_qualifying_total` is present;
   * otherwise uses full cart `total_price`. Below the lowest gift threshold, remove all gift lines;
   * below a line's tier threshold, remove that line; when `data-stack-gift-products` is not true, remove
   * lower-tier gifts once the next tier threshold is reached (tier 1 → tier 2 → tier 3, etc.).
   */
  async handleAutoDelete(event) {
    if (event.detail?.skipAutoDelete || cartAutoDeleteLock) return;

    let cart;
    try {
      const raw = event.detail?.cart;
      if (raw == null) return;
      cart = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return;
    }

    if (!cart?.items?.length) return;

    const total = getGiftEligibilityTotal(cart, this);
    const linesToRemove = [];

    const thresholds = [
      ...new Set(cart.items.map((i) => Number(i.properties?._delete_under)).filter((n) => Number.isFinite(n) && n > 0)),
    ].sort((a, b) => a - b);

    if (thresholds.length === 0) return;

    const stackGiftProducts =
      document.querySelector("[data-stack-gift-products]")?.getAttribute("data-stack-gift-products") === "true";

    cart.items.forEach((item, index) => {
      const limitRaw = item.properties?._delete_under;
      if (limitRaw == null || limitRaw === "") return;

      const limit = Number(limitRaw);
      if (!Number.isFinite(limit) || limit <= 0) return;

      const t1 = Math.round(thresholds[0]);
      if (total < t1) {
        linesToRemove.push(index + 1);
        blockGiftThreshold(limit);
        return;
      }

      const tierIdx = thresholds.findIndex((t) => Math.round(t) === Math.round(limit));
      if (tierIdx === -1) {
        if (total < Math.round(limit)) {
          linesToRemove.push(index + 1);
          blockGiftThreshold(limit);
        }
        return;
      }

      const tierMin = Math.round(thresholds[tierIdx]);
      if (total < tierMin) {
        linesToRemove.push(index + 1);
        blockGiftThreshold(limit);
        return;
      }

      if (!stackGiftProducts && tierIdx < thresholds.length - 1) {
        const nextTier = Math.round(thresholds[tierIdx + 1]);
        if (total >= nextTier) {
          linesToRemove.push(index + 1);
          // Stepping UP: lower tier removed because next tier is unlocked — do not block re-add later.
        }
      }
    });

    syncBlockedGiftThresholds(total);

    if (!linesToRemove.length) return;

    cartAutoDeleteLock = true;
    try {
      for (const line of linesToRemove.sort((a, b) => b - a)) {
        await this.updateQuantity(line, 0);
      }
    } finally {
      cartAutoDeleteLock = false;
    }
  }

  onChange(event) {
    if (event.target === null) return;
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute("name"));
  }

  getSectionsToRender() {
    let sections = [
      {
        id: "mini-cart",
        section: document.getElementById("mini-cart")?.id,
        selector: ".shopify-section",
      },
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "mobile-cart-icon-bubble",
        section: "mobile-cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "main-cart-items-sidebar",
        section: document.getElementById("main-cart-items-sidebar")?.dataset.id,
        selector: ".js-contents-sidebar",
      },
      {
        id: "sticky-checkout",
        section: document.getElementById("sticky-checkout")?.dataset.id,
        selector: ".js-contents-sticky-checkout",
      },
    ];
    if (document.querySelector("#main-cart-footer .free-shipping")) {
      sections.push({
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset.id,
        selector: ".free-shipping",
      });
    }
    return sections;
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    const sections = this.getSectionsToRender().map((section) => section.section);

    const body = JSON.stringify({
      line,
      quantity,
      sections: sections,
      sections_url: window.location.pathname,
    });

    cartCheckoutBusy.start();

    return fetch(`${theme.routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter) cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (parsedState.errors) {
          this.updateErrorLiveRegions(line, parsedState.errors);
        }
        this.renderCartSections(parsedState.sections);

        this.updateQuantityLiveRegions(line, parsedState.item_count);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && name) lineItem.querySelector(`[name="${name}"]`).focus();
        this.disableLoading();

        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: {
              cart: state,
            },
          }),
        );
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
        cartCheckoutBusy.stop();
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) => overlay.classList.add("hidden"));
        this.disableLoading();
        if (this.cartErrors) {
          this.cartErrors.textContent = theme.cartStrings.error;
        }
        cartCheckoutBusy.stop(true);
      });
  }

  renderCartSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element) return;
      const elementToReplace = element.querySelector(section.selector) || element;
      if (elementToReplace && sections?.[section.section]) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(sections[section.section], section.selector);
      }
    });
  }

  async applyLineUpdates(updates) {
    cartCheckoutBusy.start();
    try {
      const response = await fetch(`${theme.routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: this.getSectionsToRender()
            .map((section) => section.section)
            .filter(Boolean),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || `Cart update failed (${response.status})`);

      this.renderCartSections(state.sections);
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: state, skipGiftAutoLifecycle: true } }));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
      cartCheckoutBusy.stop();
      return state;
    } catch (error) {
      cartCheckoutBusy.stop(true);
      throw error;
    }
  }

  async removeExtraGifts() {
    const updates = planGiftLineUpdates(readGiftLines(this.closest("mini-cart") || this), { thresholds: false });
    if (Object.keys(updates).length) await this.applyLineUpdates(updates);
  }

  updateErrorLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") || document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  updateQuantityLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const quantityError = document.getElementById(`Line-item-error-${line}`);
      if (quantityError) {
        quantityError.querySelector(".cart-item__error-text").innerHTML = theme.cartStrings.quantityError.replace(
          "[quantity]",
          document.getElementById(`Quantity-${line}`).value,
        );
      }
    }

    this.currentItemCount = itemCount;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus = document.getElementById("cart-live-region-text");
    if (cartStatus) {
      cartStatus.setAttribute("aria-hidden", false);

      setTimeout(() => {
        cartStatus.setAttribute("aria-hidden", true);
      }, 1e3);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const cartItems = document.getElementById("main-cart-items");
    if (cartItems) cartItems.classList.add("cart__items--disabled");

    const loadingOverlay = this.querySelectorAll(".loading-overlay")[line - 1];
    if (loadingOverlay) loadingOverlay.classList.remove("hidden");

    document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading() {
    const cartItems = document.getElementById("main-cart-items");
    if (cartItems) cartItems.classList.remove("cart__items--disabled");
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);

      if (element) {
        element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
  }
}
customElements.define("cart-items", CartItems);

(function registerCartGiftAutoLifecycle() {
  if (registerCartGiftAutoLifecycle.done) return;
  registerCartGiftAutoLifecycle.done = true;

  // Serialize: finish gift removals before auto-adding the current tier.
  // Ignore cart:updated fired mid-delete so a removed higher tier is not re-added.
  let queue = Promise.resolve();

  const productHasGiftLines = (product) => {
    const items = product?.items;
    if (!Array.isArray(items)) return false;
    return items.some((item) => {
      const raw = item?.properties?._delete_under;
      return raw != null && raw !== "" && Number.isFinite(Number(raw));
    });
  };

  const enqueueDeleteThenAdd = (event, { skipAutoAdd = false } = {}) => {
    queue = queue
      .then(async () => {
        const cartItems = document.querySelector("cart-items");
        if (!cartItems) return;

        if (event) {
          await cartItems.handleAutoDelete?.(event);
        }
        await cartItems.removeExtraGifts?.();

        if (skipAutoAdd) return;

        await new Promise((resolve) => requestAnimationFrame(resolve));
        cartItems.triggerSingleGiftAutoAdd?.();
      })
      .catch((error) => {
        console.error(error);
      });

    return queue;
  };

  document.addEventListener("cart:updated", (event) => {
    if (cartAutoDeleteLock || event.detail?.skipGiftAutoLifecycle) return;
    enqueueDeleteThenAdd(event);
  });

  document.addEventListener("cartdrawer:opened", () => enqueueDeleteThenAdd(null, { skipAutoAdd: true }));

  document.addEventListener("ajaxProduct:added", (event) => {
    if (cartAutoDeleteLock) return;

    const product = event.detail?.product;
    // Gift auto-add itself should not cascade into another auto-add (that re-adds
    // a just-removed higher tier). Still run delete for lower-tier cleanup on the way up.
    const skipAutoAdd = productHasGiftLines(product);
    const cartPayload = getCartPayloadForAutoDelete(product);

    if (!cartPayload) {
      if (!skipAutoAdd) enqueueDeleteThenAdd(null);
      return;
    }

    enqueueDeleteThenAdd(new CustomEvent("cart:updated", { detail: { cart: cartPayload } }), { skipAutoAdd });
  });

  const addAutoGift = async (doc, qualifying) => {
    const button = doc.querySelector(
      'add-to-cart.motivational-bar__gift-button[data-auto-add-gift="true"]:not([disabled])',
    );
    if (!button) return;

    const properties = JSON.parse(button.dataset.customProperties || "{}");
    const tierMin = Number(properties._delete_under);
    if (!(qualifying >= tierMin) || isGiftThresholdBlocked(tierMin)) return;

    const response = await fetch(`${theme.routes.cart_add_url}`, {
      ...fetchConfig("javascript"),
      body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1, properties }] }),
    });
    if (!response.ok) throw new Error(`Gift add failed (${response.status})`);
  };

  let giftCheck = null;
  const verifyGiftsBeforeCheckout = () =>
    (giftCheck ??= (async () => {
      try {
        await queue;
        const cartItems = document.querySelector("cart-items");
        if (!cartItems) return;

        const sections = await fetch(`${theme.routes.root_url}?sections=mini-cart`).then((response) => response.json());
        let doc = new DOMParser().parseFromString(sections["mini-cart"] || "", "text/html");
        const qualifying = getGiftEligibilityTotal({}, doc);
        const stack = doc.querySelector("[data-stack-gift-products]")?.getAttribute("data-stack-gift-products") === "true";
        syncBlockedGiftThresholds(qualifying);

        cartAutoDeleteLock = true;
        const updates = planGiftLineUpdates(readGiftLines(doc), { qualifying, stack, thresholds: true });
        if (Object.keys(updates).length) {
          const state = await cartItems.applyLineUpdates(updates);
          doc = new DOMParser().parseFromString(state.sections?.["mini-cart"] || "", "text/html");
        }
        await addAutoGift(doc, qualifying);
      } catch (error) {
        console.error("Gift check before checkout failed:", error);
      } finally {
        cartAutoDeleteLock = false;
        giftCheck = null;
      }
    })());

  window.verifyGiftsBeforeCheckout = verifyGiftsBeforeCheckout;

  const boot = () => {
    enqueueDeleteThenAdd(getCartPageAutoGiftEvent());
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${theme.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, 300),
    );
  }
}
customElements.define("cart-note", CartNote);

class DiscountCode extends HTMLElement {
  constructor() {
    super();

    if (isStorageSupported("session")) {
      this.setupDiscount();

      this.addEventListener("change", (event) => {
        window.sessionStorage.setItem("discount", event.target.value);
      });
    }
  }

  setupDiscount() {
    const discount = window.sessionStorage.getItem("discount");
    if (discount !== null) {
      this.querySelector('input[name="discount"]').value = discount;
    }
  }
}

customElements.define("discount-code", DiscountCode);

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.setupCountries();

    this.errors = this.querySelector("#ShippingCalculatorErrors");
    this.success = this.querySelector("#ShippingCalculatorSuccess");
    this.zip = this.querySelector("#ShippingCalculatorZip");
    this.country = this.querySelector("#ShippingCalculatorCountry");
    this.province = this.querySelector("#ShippingCalculatorProvince");
    this.button = this.querySelector("button");
    this.button.addEventListener("click", this.onSubmitHandler.bind(this));
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      // eslint-disable-next-line no-new
      new Shopify.CountryProvinceSelector("ShippingCalculatorCountry", "ShippingCalculatorProvince", {
        hideElement: "ShippingCalculatorProvinceContainer",
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();

    this.errors.classList.add("hidden");
    this.success.classList.add("hidden");
    this.zip.classList.remove("invalid");
    this.country.classList.remove("invalid");
    this.province.classList.remove("invalid");
    this.button.classList.add("loading");
    this.button.setAttribute("disabled", true);

    const body = JSON.stringify({
      shipping_address: {
        zip: this.zip.value,
        country: this.country.value,
        province: this.province.value,
      },
    });
    let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace("//", "/");

    fetch(sectionUrl, { ...fetchConfig("javascript"), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.success.classList.remove("hidden");
          this.success.innerHTML = "";

          parsedState.shipping_rates.forEach((rate) => {
            const child = document.createElement("p");
            child.innerHTML = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
            this.success.appendChild(child);
          });
        } else {
          let errors = [];
          Object.entries(parsedState).forEach(([attribute, messages]) => {
            errors.push(`${attribute.charAt(0).toUpperCase() + attribute.slice(1)} ${messages[0]}`);
          });

          this.errors.classList.remove("hidden");
          this.errors.querySelector(".errors").innerHTML = errors.join("; ");
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove("loading");
        this.button.removeAttribute("disabled");
      });
  }
}

customElements.define("shipping-calculator", ShippingCalculator);

if (!customElements.get("discount-coupon")) {
  class DiscountCoupon extends HTMLElement {
    constructor() {
      super();

      this.input = null;
      this.button = null;
      this.buttonText = null;
      this.errorContainer = null;
      this.errorMessage = null;
      this.spinner = null;
      this.cartItems = null;
      this.activeCode = "";
      this.isSubmitting = false;

      this.submitHandler = this.handleSubmit.bind(this);
      this.focusHandler = this.clearErrorState.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector(".discount-input");
      this.button = this.querySelector(".discount-submit");
      this.buttonText = this.querySelector(".discount-submit .button__text");
      this.errorContainer = this.querySelector(".error-discount");
      this.errorMessage = this.querySelector("[data-discount-error-message]");
      this.spinner = this.querySelector(".discount-submit .button__spinner");
      this.cartItems = document.querySelector("cart-items");

      if (this.button) {
        this.button.addEventListener("click", this.submitHandler);
      }

      if (this.input) {
        this.input.addEventListener("focus", this.focusHandler);
        this.input.addEventListener("keydown", this.keydownHandler);
        this.activeCode = this.input.value.trim();
      }

      this.updateButtonState();
    }

    disconnectedCallback() {
      if (this.button) {
        this.button.removeEventListener("click", this.submitHandler);
      }

      if (this.input) {
        this.input.removeEventListener("focus", this.focusHandler);
        this.input.removeEventListener("keydown", this.keydownHandler);
      }
    }

    handleKeydown(event) {
      if (event.key === "Enter") {
        this.handleSubmit(event);
      }
    }

    clearErrorState() {
      this.setErrorState(false);
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting || !this.button) return;

      const mode = this.button.dataset.mode;
      const isRemoval = mode === "remove";

      if (isRemoval) {
        if (!this.activeCode) {
          return;
        }
        await this.updateDiscount("");
        return;
      }

      if (!this.input) return;

      const nextCode = this.input.value.trim();
      if (!nextCode) {
        this.setErrorState(true, null, "Enter a coupon code before applying.");
        return;
      }

      await this.updateDiscount(nextCode);
    }

    getSectionsPayload() {
      const cartItems = this.cartItems || document.querySelector("cart-items");
      if (!cartItems || typeof cartItems.getSectionsToRender !== "function") {
        return { cartItems: null, sectionsConfig: [], sections: [] };
      }

      const sectionsConfig = cartItems.getSectionsToRender().filter((section) => section.section);
      const sections = sectionsConfig.map((section) => section.section);

      return { cartItems, sectionsConfig, sections };
    }

    async updateDiscount(nextCode) {
      const { cartItems, sectionsConfig, sections } = this.getSectionsPayload();

      if (!cartItems || sections.length === 0) {
        console.warn("Unable to resolve cart sections for discount update.");
        return;
      }

      this.clearErrorState();
      this.setLoadingState(true);

      try {
        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: nextCode,
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const stateText = await response.text();
        const parsedState = stateText ? JSON.parse(stateText) : {};

        const discountCodes = Array.isArray(parsedState.discount_codes) ? parsedState.discount_codes : [];
        const appliedCode = discountCodes.find((codeObj) => codeObj?.applicable)?.code?.trim() || "";
        const attemptedCode = (nextCode || "").trim();
        const attemptedEntry = attemptedCode
          ? discountCodes.find((codeObj) => codeObj?.code?.trim()?.toLowerCase() === attemptedCode.toLowerCase())
          : null;

        if (!response.ok) {
          const rawMessage = parsedState?.message || parsedState?.errors?.discount || parsedState?.errors;
          const message = Array.isArray(rawMessage) ? rawMessage[0] : rawMessage;
          if (this.input && attemptedCode) {
            this.input.value = attemptedCode;
          }
          this.activeCode = appliedCode;
          this.updateButtonState();
          this.setErrorState(true, attemptedCode, message);
          return;
        }

        if (attemptedCode && attemptedEntry && attemptedEntry.applicable === false) {
          if (this.input) {
            this.input.value = attemptedCode;
          }
          this.activeCode = appliedCode;
          this.updateButtonState();
          this.setErrorState(true, attemptedCode);
          return;
        }

        this.renderSections(parsedState, cartItems, sectionsConfig);

        this.activeCode = appliedCode;
        this.syncInputFromActiveCode();
        this.updateButtonState();
      } catch (error) {
        console.error("Error updating discount code:", error);
        this.setErrorState(true, nextCode, "Unable to update coupon. Please try again.");
      } finally {
        this.setLoadingState(false);
      }
    }

    renderSections(parsedState, cartItems, sectionsConfig) {
      const isEmpty = parsedState.item_count === 0;
      cartItems.classList.toggle("is-empty", isEmpty);

      const cartFooter = document.getElementById("main-cart-footer");
      if (cartFooter) {
        cartFooter.classList.toggle("is-empty", isEmpty);
      }

      sectionsConfig.forEach((section) => {
        const element = document.getElementById(section.id);
        if (!element || !parsedState.sections?.[section.section]) return;

        const elementToReplace = element.querySelector(section.selector) || element;
        const html = cartItems.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (typeof html === "string") {
          elementToReplace.innerHTML = html;
        }
      });

      this.cartItems = document.querySelector("cart-items");
    }

    syncInputFromActiveCode() {
      if (!this.input) return;

      this.input.value = this.activeCode || "";
      if (this.activeCode) {
        this.input.classList.remove("invalid");
        this.input.removeAttribute("aria-invalid");
      }
    }

    updateButtonState() {
      const hasActiveCode = Boolean(this.activeCode);
      const mode = hasActiveCode ? "remove" : "apply";

      if (this.button) {
        this.button.dataset.mode = mode;
        this.button.setAttribute("aria-label", hasActiveCode ? "Remove discount code" : "Apply discount code");
      }

      if (this.buttonText) {
        const applyText = this.buttonText.dataset.textApply || "Apply";
        const removeText = this.buttonText.dataset.textRemove || "Remove";
        this.buttonText.textContent = hasActiveCode ? removeText : applyText;
      }
    }

    setErrorState(hasError, attemptedCode = "", message = "") {
      const fallbackMessage = message
        ? message
        : attemptedCode
          ? `Coupon “${attemptedCode}” can't be applied.`
          : "Coupon code could not be updated. Please try again.";

      if (this.input) {
        this.input.classList.toggle("invalid", !!hasError);
        if (hasError) {
          this.input.setAttribute("aria-invalid", "true");
        } else {
          this.input.removeAttribute("aria-invalid");
        }
      }

      if (!this.errorContainer) return;

      this.errorContainer.hidden = !hasError;

      if (hasError) {
        if (this.errorMessage) {
          this.errorMessage.textContent = fallbackMessage;
        } else {
          this.errorContainer.textContent = fallbackMessage;
        }
      } else if (this.errorMessage) {
        this.errorMessage.textContent = "";
      }
    }

    setLoadingState(isLoading) {
      this.isSubmitting = isLoading;

      if (this.button) {
        this.button.disabled = isLoading;
        this.button.classList.toggle("is-loading", isLoading);
        this.button.setAttribute("aria-disabled", isLoading ? "true" : "false");
      }

      if (this.buttonText) {
        this.buttonText.style.opacity = isLoading ? "0" : "1";
      }

      if (this.spinner) {
        this.spinner.classList.toggle("hidden", !isLoading);
        this.spinner.setAttribute("aria-hidden", (!isLoading).toString());
      }
    }
  }

  customElements.define("discount-coupon", DiscountCoupon);
}

// Gift Tooltip and Modal functionality
if (!customElements.get("gift-info-modal")) {
  class GiftInfoModal extends HTMLElement {
    constructor() {
      super();
      this.iconSelector = this.getAttribute("icon-selector") || "#gift-info-icon";
      this.modalSelector = this.getAttribute("modal-selector") || "#gift-modal";
      this.overlaySelector = this.getAttribute("overlay-selector") || "#gift-modal-overlay";
      this.closeSelector = this.getAttribute("close-selector") || "#gift-modal-close";

      this.onIconClick = this.onIconClick.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onEsc = this.onEsc.bind(this);
      this.rebind = this.rebind.bind(this);
    }

    connectedCallback() {
      this.rebind();
      document.addEventListener("cart:updated", this.rebind);
    }

    disconnectedCallback() {
      document.removeEventListener("cart:updated", this.rebind);
      this.teardown();
    }

    rebind() {
      this.teardown();
      this.icon = document.querySelector(this.iconSelector);
      this.modal = document.querySelector(this.modalSelector);
      this.overlay = document.querySelector(this.overlaySelector);
      this.closeButton = document.querySelector(this.closeSelector);

      if (!this.icon || !this.modal) return;

      // Desktop hover handled via CSS; only capture click for mobile
      this.icon.addEventListener("click", this.onIconClick);
      if (this.overlay) this.overlay.addEventListener("click", this.onOverlayClick);
      if (this.closeButton) this.closeButton.addEventListener("click", this.onCloseClick);
      document.addEventListener("keydown", this.onEsc);
    }

    teardown() {
      if (this.icon) this.icon.removeEventListener("click", this.onIconClick);
      if (this.overlay) this.overlay.removeEventListener("click", this.onOverlayClick);
      if (this.closeButton) this.closeButton.removeEventListener("click", this.onCloseClick);
      document.removeEventListener("keydown", this.onEsc);
    }

    onIconClick(event) {
      if (window.innerWidth > 749) return;
      event.preventDefault();
      event.stopPropagation();
      this.openModal();
    }

    onOverlayClick() {
      this.closeModal();
    }

    onCloseClick() {
      this.closeModal();
    }

    onEsc(event) {
      if (event.key === "Escape" && this.modal?.classList.contains("active")) {
        this.closeModal();
      }
    }

    openModal() {
      if (!this.modal) return;
      this.modal.classList.add("active");
      document.body.style.overflow = "hidden";
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove("active");
      document.body.style.overflow = "";
    }
  }

  customElements.define("gift-info-modal", GiftInfoModal);
}

class CartTermsAcceptance extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
  }

  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    if (!this.checkbox) return;

    this.checkbox.addEventListener("change", this.onChange);
    this.syncCheckoutState();
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener("change", this.onChange);
  }

  onChange() {
    this.syncCheckoutState();
  }

  onCartUpdated() {
    this.syncCheckoutState();
  }

  syncCheckoutState() {
    const checkbox = this.checkbox || this.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    const container = this.closest(".button-container, .summary_buttons, .js-contents-sticky-checkout");
    const checkoutBtn = container?.querySelector('button[name="checkout"], button[data-delivery-address-open]');
    if (!checkoutBtn || checkoutBtn.dataset.cartEmpty === "true") return;
    if (cartCheckoutBusy.isBusy) return;

    const enabled = checkbox.checked;
    checkoutBtn.disabled = !enabled;
    checkoutBtn.setAttribute("aria-disabled", String(!enabled));
  }
}

customElements.define("cart-terms-acceptance", CartTermsAcceptance);

// OTP Login Trigger functionality (using event delegation for dynamically loaded content)
document.addEventListener("click", function (event) {
  const loginButton = event.target.closest("#otp-login-trigger");
  if (!loginButton) return;

  event.preventDefault();
  if (window.showOTPLoginPopup) {
    window.showOTPLoginPopup();
  } else {
    console.error("OTP Login Popup not found. Make sure the OTP Login Popup block is added to your theme.");
  }
});
